import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { ChapterHeading } from "@/components/ChapterHeading";
import { Globe, GraduationCap, Info, LifeBuoy, Lock, ShieldCheck, Users } from "lucide-react";

const AboutPage = () => {
  const { t } = useLanguage();
  const a = t.aboutPage;

  useEffect(() => {
    document.title = a.metaTitle;
  }, [a]);

  return (
    <main data-testid="about-page">
      <section className="relative overflow-hidden border-b border-border bg-white">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center gap-3 opacity-[0.05]">
          {[30, 48, 66, 42, 58, 36].map((h, i) => (
            <div key={i} className="w-10 rounded-t-lg bg-primary sm:w-16" style={{ height: `${h}vh` }} />
          ))}
        </div>
        <div className="relative mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-accent px-4 py-1.5 text-xs font-bold tracking-wide text-primary"
            data-testid="about-badge"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
            {a.badge}
          </motion.p>
          <h1 className="text-balance text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl" data-testid="about-title">
            <span className="mask-line">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {a.title}
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg"
            data-testid="about-intro"
          >
            {a.intro}
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20 sm:px-8" data-testid="about-purpose-section">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-lg border border-border bg-card p-8 shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid="about-purpose-card">
              <GraduationCap className="h-8 w-8 text-primary" aria-hidden="true" />
              <h2 className="mt-4 text-xl font-bold text-foreground">{a.purposeTitle}</h2>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{a.purpose}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-lg border border-border bg-card p-8 shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid="about-bilingual-card">
              <Globe className="h-8 w-8 text-primary" aria-hidden="true" />
              <h2 className="mt-4 text-xl font-bold text-foreground">{a.bilingualTitle}</h2>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{a.bilingual}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-border bg-white" data-testid="houses-section">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8">
          <Reveal>
            <ChapterHeading number="01" title={a.housesTitle} intro={a.housesIntro} />
          </Reveal>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="houses-grid">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <Reveal key={n} delay={n * 0.06}>
                <div className="group flex h-full flex-col items-center rounded-lg border border-border bg-background p-6 text-center shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`house-card-${n}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-lg font-extrabold text-primary transition-colors duration-200 group-hover:bg-primary group-hover:text-primary-foreground">
                    {n}
                  </span>
                  <p className="mt-3 text-sm font-bold text-foreground">
                    {a.house} {n}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20 sm:px-8" data-testid="access-section">
        <Reveal>
          <ChapterHeading number="02" title={a.accessTitle} />
        </Reveal>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Reveal delay={0.05}>
            <div className="h-full rounded-lg border border-border bg-card p-8 shadow-sm" data-testid="access-public-card">
              <Users className="h-8 w-8 text-primary" aria-hidden="true" />
              <h3 className="mt-4 text-lg font-bold text-foreground">{a.publicTitle}</h3>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{a.publicDesc}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="h-full rounded-lg border border-border bg-card p-8 shadow-sm" data-testid="access-staff-card">
              <Lock className="h-8 w-8 text-primary" aria-hidden="true" />
              <h3 className="mt-4 text-lg font-bold text-foreground">{a.staffTitle}</h3>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{a.staffDesc}</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="mt-14 rounded-lg border border-primary/20 bg-accent/40 p-8" data-testid="about-links-card">
            <h3 className="text-lg font-bold text-foreground">{a.linksTitle}</h3>
            <div className="mt-5 flex flex-wrap gap-4">
              <Link
                to="/"
                data-testid="about-to-support-link"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-md transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-lg"
              >
                <LifeBuoy className="h-4 w-4" aria-hidden="true" />
                {a.toSupport}
              </Link>
              <Link
                to="/privacy"
                data-testid="about-to-privacy-link"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-6 py-3 text-sm font-bold text-foreground shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md"
              >
                <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
                {a.toPrivacy}
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default AboutPage;
